import React, { useEffect, useState } from "react";
// @material-ui/core components
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Skeleton from "react-loading-skeleton";
// core components
import { useSubscription } from "store/auth/auth-store";

import { ColumnDef } from "@tanstack/react-table";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useGetWorkers } from "services/input-core/workers/get-workers-paginated";
import { getBadgeFromStatus, getFilters, getSorting } from "utils/functions";

import { People, Search } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import Card from "components/UI/Card/Card.js";
import CardBody from "components/UI/Card/CardBody.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import FieldglassButton from "components/UI/FieldglassButton";
import { TitlePages } from "components/UI/TitlePage/TitlePages";
import { FilterWorkerStatusEnum, RolesEnum } from "utils/@types";
import useHasRole from "hooks/useHasRole";
import { useWorkerStore } from "store/workers";

export default function Workers() {
  const { t } = useTranslation("worker-profile");
  const { isElCorteIngles, isAcelen } = useSubscription();
  const { setWorkers, typeWorker, setTypeWorker } = useWorkerStore();

  const { hasResourceRole } = useHasRole();
  const userBuyer = hasResourceRole([RolesEnum.BUYER]);

  const { pathname } = useLocation();

  useEffect(() => {
    setTypeWorker(pathname.split("/")[2] === "equipments" ? 1 : 0);
  }, [pathname, setTypeWorker]);

  const [tableData, setTableData] = useState({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const paramsGetWorker = {
    "page[size]": tableData.pageSize,
    "page[index]": tableData.pageIndex,
    ...getFilters(tableData.columnFilters),
    ...getSorting(tableData.sorting),
  };

  const { data, isLoading, isFetching } = useGetWorkers(
    isAcelen
      ? { ...paramsGetWorker, "filter[equipment]": typeWorker }
      : paramsGetWorker,
    {
      onSuccess: (response) => {
        setWorkers(response.records);
      },
      staleTime: 0,
    }
  );

  const columns: ColumnDef<any, any>[] = [
    {
      footer: (props: any) => props.column.status,
      header: t("worker-table-status"),
      accessorKey: "status",
      cell: ({ row }: any) => getBadgeFromStatus(row.original?.status, t),
    },
    {
      footer: (props: any) => props.column.id,
      header: t("worker-table-id"),
      accessorKey: "workerID",
    },
    {
      header: typeWorker
        ? t("equipment-table-brand")
        : t("worker-table-first-name"),
      footer: (props: any) => props.column.id,
      accessorKey: typeWorker ? "lastName" : "firstName",
    },
    {
      header: typeWorker
        ? t("equipment-table-model")
        : t("worker-table-last-name"),
      footer: (props: any) => props.column.id,
      accessorKey: typeWorker ? "firstName" : "lastName",
    },
    typeWorker
      ? {
          header: t("equipment-table-car-plate"),
          footer: (props: any) => props.column.id,
          accessorKey: "carPlate",
        }
      : null,
    {
      header: t("worker-table-email"),
      footer: (props: any) => props.column.id,
      accessorKey: "workerEmail",
    },
    isElCorteIngles === true
      ? {
          header: "DNI",
          footer: (props: any) => props.column.id,
          accessorKey: "internalFields",
          cell: ({ row }: any) => (
            <div>
              {row.original?.internalFields?.length > 0
                ? row.original?.internalFields[0].documentNumber
                : ""}
            </div>
          ),
        }
      : null,
    userBuyer && {
      header: t("provider-id"),
      footer: (props: any) => props.column.id,
      accessorKey: "vendorNumber",
    },
    userBuyer && {
      header: t("provider"),
      footer: (props: any) => props.column.id,
      accessorKey: "vendorName",
    },
    {
      header: t("worker-table-action"),
      accessorKey: "actions",
      cell: ({ row }: any) => {
        return (
          <div className="actions-right">
            {row?.original?.consolidatedWorkerRef && (
              <Tooltip placement="top" title={t("go-to-consolidated-worker")}>
                <Link
                  to={
                    "/app/consolidatedworkers/profile?id=" +
                    row?.original?.consolidatedWorkerRef?.consolidatedWorkerID
                  }
                >
                  <ButtonTS
                    round
                    justIcon
                    size="sm"
                    color="warning"
                    key={new Date().getTime()}
                  >
                    <People />
                  </ButtonTS>
                </Link>
              </Tooltip>
            )}

            <Tooltip placement="top" title={t("go-to-worker")}>
              <Link to={"/app/workers/profile?id=" + row?.original?.workerID}>
                <ButtonTS
                  round
                  justIcon
                  size="sm"
                  color="info"
                  key={new Date().getTime()}
                >
                  <ArrowForwardIosIcon />
                </ButtonTS>
              </Link>
            </Tooltip>
          </div>
        );
      },
      enableColumnFilter: false,
    },
  ].filter((element) => element);

  return (
    <Card>
      <CardHeader color="rose" icon>
        <CardIcon color="rose">
          <Search />
        </CardIcon>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <TitlePages
            type="sidebar"
            title={t(!typeWorker ? "workers" : "equipments")}
          />
          <FieldglassButton type="worker" />
        </div>
      </CardHeader>
      <CardBody>
        {isLoading || isFetching ? (
          <Skeleton />
        ) : (
          <PaginatedTable
            id="workers-table"
            columns={columns}
            data={data?.records || []}
            tableState={tableData}
            pageCount={data?.page_count}
            setTableState={setTableData}
            fixedPageSize
            statusToFilter={FilterWorkerStatusEnum}
          />
        )}
      </CardBody>
    </Card>
  );
}
